// pages/PricingDashboard.jsx
import { useCallback, useContext, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCcw } from "lucide-react";
import { UserContext } from "../context/UserContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { getSubscription, changeSubscriptionPlan } from "../api/subscriptions";
import PricingCards, {
  SUBSCRIPTION_PLAN_DEFINITIONS,
  SUBSCRIPTION_PLAN_PRICES,
} from "../components/PricingCards";
import { errorMessage } from "../utils/errorMessage";
import { formatPersianNumber as toPersianPrice } from "../utils/persianNumber";

const STATUS_META = {
  active: { label: "فعال", className: "bg-secondary_1 text-secondary" },
  expired: { label: "منقضی‌شده", className: "bg-red-100 text-red-600" },
  inactive: { label: "غیرفعال", className: "bg-gray-100 text-gray-600" },
};

const planLabel = (planId) => SUBSCRIPTION_PLAN_DEFINITIONS.find((p) => p.id === planId)?.title || planId;

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("fa-IR", { year: "numeric", month: "long", day: "numeric" }).format(new Date(value))
    : null;

export default function PricingDashboard() {
  const { user } = useContext(UserContext);
  const restaurantId = user?._id;

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [billing, setBilling] = useState("monthly");
  const [confirmPlanId, setConfirmPlanId] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // App-wide plan state (used by locked pages); refreshed after a plan change
  // so newly included features unlock without a page reload.
  const { refresh: refreshEntitlements } = useEntitlements();

  const loadSubscription = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setLoadError("");
    try {
      const data = await getSubscription(restaurantId);
      setSubscription(data);
      setBilling(data.plan !== "free" ? data.billingCycle : "monthly");
    } catch (error) {
      setLoadError(errorMessage(error, "دریافت اطلاعات اشتراک با خطا مواجه شد."));
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handleSelectPlan = (planId) => {
    setActionError("");
    setConfirmPlanId(planId);
  };

  const closeConfirm = () => {
    if (processingPlanId) return;
    setConfirmPlanId(null);
    setActionError("");
  };

  const confirmPlanChange = async () => {
    if (!confirmPlanId) return;
    setProcessingPlanId(confirmPlanId);
    setActionError("");
    try {
      const updated = await changeSubscriptionPlan(restaurantId, { plan: confirmPlanId, billingCycle: billing });
      setSubscription(updated);
      refreshEntitlements();
      setConfirmPlanId(null);
      setSuccessMessage(`پلن شما با موفقیت به «${planLabel(confirmPlanId)}» تغییر یافت.`);
    } catch (error) {
      setActionError(errorMessage(error, "تغییر پلن با خطا مواجه شد. لطفاً دوباره تلاش کنید."));
    } finally {
      setProcessingPlanId(null);
    }
  };

  const isDowngradeToFree = confirmPlanId === "free" && subscription?.plan !== "free";
  const confirmPrice = confirmPlanId ? SUBSCRIPTION_PLAN_PRICES[confirmPlanId][billing] : 0;

  return (
    <div className="p-2 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-accent">پلن و تعرفه‌ها</h1>
        <p className="text-gray-500 mt-1 text-sm">
          پلن مناسب کسب‌وکار خودتان را انتخاب کنید و هر زمان که خواستید آن را تغییر دهید.
        </p>
      </div>

      {/* پیام موفقیت */}
      {successMessage && (
        <div className="flex items-center gap-2 bg-secondary_1 text-secondary border border-secondary/20 rounded-xl px-4 py-3 mb-6 text-sm">
          <CheckCircle2 size={18} className="shrink-0" />
          {successMessage}
        </div>
      )}

      {/* وضعیت بارگذاری */}
      {loading && (
        <div className="space-y-6 animate-pulse">
          <div className="h-24 bg-gray-100 rounded-2xl" />
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[520px] bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
      )}

      {/* خطای بارگذاری */}
      {!loading && loadError && (
        <div className="flex flex-col items-center justify-center gap-3 bg-red-50 border border-red-100 rounded-2xl p-10 text-center">
          <AlertTriangle className="text-red-500" size={28} />
          <p className="text-red-600 text-sm">{loadError}</p>
          <button
            onClick={loadSubscription}
            className="flex items-center gap-2 text-sm font-medium text-white bg-secondary px-4 py-2 rounded-lg hover:opacity-90"
          >
            <RefreshCcw size={14} />
            تلاش دوباره
          </button>
        </div>
      )}

      {!loading && !loadError && subscription && (
        <>
          {/* وضعیت فعلی */}
          <div className="bg-white border rounded-2xl p-5 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">پلن فعلی شما</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-bold text-accent">{planLabel(subscription.plan)}</span>
                <span className={`text-xs px-3 py-1 rounded-md font-medium ${STATUS_META[subscription.status]?.className || STATUS_META.inactive.className}`}>
                  {STATUS_META[subscription.status]?.label || subscription.status}
                </span>
                {subscription.plan !== "free" && (
                  <span className="text-xs text-gray-500">
                    دوره {subscription.billingCycle === "yearly" ? "سالانه" : "ماهانه"}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm">
              {subscription.plan === "free" ? (
                <p className="text-gray-500">پلن رایگان محدودیت زمانی ندارد.</p>
              ) : (
                <>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">شروع دوره</p>
                    <p className="text-accent font-medium">{formatDate(subscription.startDate) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">پایان دوره</p>
                    <p className="text-accent font-medium">{formatDate(subscription.endDate) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-0.5">
                      {subscription.isExpired ? "وضعیت" : "روزهای باقی‌مانده"}
                    </p>
                    <p className={`font-bold ${subscription.isExpired ? "text-red-500" : "text-secondary"}`}>
                      {subscription.isExpired ? "منقضی شده" : `${toPersianPrice(subscription.daysRemaining)} روز`}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {subscription.isExpired && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 mb-8 text-sm">
              <AlertTriangle size={18} className="shrink-0" />
              اشتراک شما منقضی شده است. برای ادامه استفاده از امکانات این پلن، آن را تمدید یا پلن دیگری انتخاب کنید.
            </div>
          )}

          {/* toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
              {["monthly", "yearly"].map((type) => (
                <button
                  key={type}
                  onClick={() => setBilling(type)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition ${
                    billing === type ? "bg-secondary text-white" : "text-gray-600"
                  }`}
                >
                  {type === "monthly" ? "ماهانه" : "سالانه (۲ ماه رایگان)"}
                </button>
              ))}
            </div>
          </div>

          <PricingCards
            billing={billing}
            activePlan={subscription.plan}
            activeBillingCycle={subscription.billingCycle}
            onSelect={handleSelectPlan}
            processingPlanId={processingPlanId}
            dashboardMode
          />
        </>
      )}

      {/* مودال تایید تغییر پلن */}
      {confirmPlanId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold text-accent mb-2">تایید تغییر پلن</h2>
            <p className="text-sm text-gray-600 mb-4">
              شما در حال تغییر پلن به{" "}
              <span className="font-bold text-accent">«{planLabel(confirmPlanId)}»</span>{" "}
              با دوره {billing === "yearly" ? "سالانه" : "ماهانه"} و قیمت{" "}
              <span className="font-bold text-accent">
                {confirmPrice === 0 ? "رایگان" : `${toPersianPrice(confirmPrice)} تومان`}
              </span>{" "}
              هستید.
            </p>

            {isDowngradeToFree && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 text-amber-700 rounded-xl px-3 py-2 mb-4 text-xs">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                با بازگشت به پلن رایگان، امکاناتی مثل رزرو آنلاین میز، باشگاه مشتریان و نقشه سالن برای مشتریان شما غیرفعال می‌شود.
              </div>
            )}

            {actionError && (
              <p className="text-xs text-red-600 mb-4">{actionError}</p>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={closeConfirm}
                disabled={Boolean(processingPlanId)}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                انصراف
              </button>
              <button
                onClick={confirmPlanChange}
                disabled={Boolean(processingPlanId)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-secondary text-white hover:opacity-90 disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {processingPlanId && <Loader2 size={16} className="animate-spin" />}
                تایید و فعال‌سازی
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
